import React, { useState } from 'react';
import { CheckCircle2, Ban, Pause, Play, Power, XCircle } from 'lucide-react';
import {
  ClienteAtivacaoBloqueadaError,
  SituacaoCadastral,
  TransicaoCliente,
  type PendingItem,
} from '../api';
import { useTransicionarCliente } from '../hooks';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { Textarea } from '../../../shared/components/Textarea';

interface Props {
  clientId: string;
  situacao: SituacaoCadastral;
}

const TRANSICOES_POR_SITUACAO: Record<SituacaoCadastral, TransicaoCliente[]> = {
  em_cadastro: ['ativar'],
  ativo: ['suspender', 'bloquear', 'inativar', 'encerrar'],
  suspenso: ['reativar', 'bloquear', 'encerrar'],
  bloqueado: ['reativar', 'encerrar'],
  inativo: ['reativar', 'encerrar'],
  encerrado: [],
};

const TRANSITION_META: Record<TransicaoCliente, {
  label: string;
  icon: React.ReactNode;
  variant: 'primary' | 'warning' | 'danger' | 'neutral';
  requiresConfirmation: boolean;
  askReason: boolean;
}> = {
  ativar: {
    label: 'Ativar cliente',
    icon: <CheckCircle2 size={16} />,
    variant: 'primary',
    requiresConfirmation: false,
    askReason: false,
  },
  reativar: {
    label: 'Reativar',
    icon: <Play size={16} />,
    variant: 'primary',
    requiresConfirmation: false,
    askReason: false,
  },
  suspender: {
    label: 'Suspender',
    icon: <Pause size={16} />,
    variant: 'warning',
    requiresConfirmation: true,
    askReason: true,
  },
  inativar: {
    label: 'Inativar',
    icon: <Power size={16} />,
    variant: 'neutral',
    requiresConfirmation: true,
    askReason: true,
  },
  bloquear: {
    label: 'Bloquear',
    icon: <Ban size={16} />,
    variant: 'danger',
    requiresConfirmation: true,
    askReason: true,
  },
  encerrar: {
    label: 'Encerrar (terminal)',
    icon: <XCircle size={16} />,
    variant: 'danger',
    requiresConfirmation: true,
    askReason: true,
  },
};

const VARIANT_CLASSES: Record<string, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  neutral: 'bg-gray-600 text-white hover:bg-gray-700',
};

const MISSING_LABEL: Record<string, string> = {
  enderecoPrincipal: 'Endereço principal cadastrado',
  segmentoMercado: 'Segmento de mercado',
  razaoSocial: 'Razão social',
  documento: 'CNPJ/CPF',
  tipoPessoa: 'Tipo de pessoa',
};

/**
 * Painel de transições de situação cadastral (Fase 2). Renderiza só as
 * ações permitidas pela máquina de estados a partir do estado atual.
 * Bloquear/inativar/encerrar/suspender pedem confirmação com motivo;
 * ativar/reativar são one-click.
 */
export const ClienteSituacaoActions: React.FC<Props> = ({ clientId, situacao }) => {
  const transicionar = useTransicionarCliente();
  const [pending, setPending] = useState<TransicaoCliente | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);

  const disponiveis = TRANSICOES_POR_SITUACAO[situacao] ?? [];

  const executar = async (action: TransicaoCliente, motivo?: string) => {
    setError(null);
    setMissing([]);
    setPendingItems([]);
    try {
      await transicionar.mutateAsync({ id: clientId, action, reason: motivo });
      setSuccess(`Transição "${TRANSITION_META[action].label}" concluída.`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      if (err instanceof ClienteAtivacaoBloqueadaError) {
        setMissing(err.missing);
        setPendingItems(err.pendingItems);
        return;
      }
      setError(getFriendlyErrorMessage(err, 'Falha ao executar transição.'));
    }
  };

  const handleClick = (action: TransicaoCliente) => {
    const meta = TRANSITION_META[action];
    if (meta.requiresConfirmation) {
      setPending(action);
      setReason('');
    } else {
      void executar(action);
    }
  };

  const confirm = () => {
    if (!pending) return;
    const meta = TRANSITION_META[pending];
    void executar(pending, meta.askReason && reason.trim() ? reason.trim() : undefined);
    setPending(null);
  };

  if (disponiveis.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        Cliente <strong>encerrado</strong> — sem transições disponíveis (estado terminal).
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {disponiveis.map((action) => {
          const meta = TRANSITION_META[action];
          return (
            <button
              key={action}
              type="button"
              onClick={() => handleClick(action)}
              disabled={transicionar.isPending}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${VARIANT_CLASSES[meta.variant]}`}
            >
              {meta.icon}
              {meta.label}
            </button>
          );
        })}
      </div>

      {success && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {(pendingItems.length > 0 || missing.length > 0) && (
        <div
          className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800"
          data-testid="ativacao-bloqueada"
        >
          <strong>Cliente não pode ser ativado — pendências:</strong>
          <ul className="mt-1 list-inside list-disc">
            {pendingItems.length > 0
              ? pendingItems.map((p) => <li key={p.code}>{p.message}</li>)
              : missing.map((m) => <li key={m}>{MISSING_LABEL[m] ?? m}</li>)}
          </ul>
          {situacao === 'em_cadastro' && (
            <p className="mt-2 text-xs text-yellow-700">
              O cadastro segue salvo como <em>rascunho</em> (situação "Em Cadastro"). Você pode
              retornar depois quando as pendências forem resolvidas.
            </p>
          )}
        </div>
      )}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmar: {TRANSITION_META[pending].label}
              </h3>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-sm text-gray-700">
                Esta ação altera o ciclo de vida do cliente e pode impactar as operações
                comerciais.
              </p>
              {TRANSITION_META[pending].askReason && (
                <Textarea
                  label="Motivo (opcional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
              <button
                type="button"
                onClick={() => setPending(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={transicionar.isPending}
                className={`rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${VARIANT_CLASSES[TRANSITION_META[pending].variant]}`}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
